// Component: SearchableListbox.jsx
// Purpose: One accessible grouped editable combobox/listbox for Tree add, operator and operand choices.

import React from 'react';

const restoreFocus = (openerRef) => {
  const opener = openerRef?.current || openerRef;
  const focus = () => opener?.focus?.();
  if (typeof queueMicrotask === 'function') queueMicrotask(focus);
  else Promise.resolve().then(focus);
};

const optionText = (option) => [
  option.label,
  option.id,
  option.searchText,
  ...(option.keywords || []),
].filter(Boolean).join(' ').toLowerCase();

export default function SearchableListbox({
  ariaLabel,
  groups = [],
  openerRef,
  onSelect,
  onClose,
  renderOption,
  emptyLabel = 'No matching options.',
  placeholder = 'Type to search',
  initialQuery = '',
  className = '',
}) {
  const instanceId = React.useId().replace(/:/g, '');
  const inputRef = React.useRef(null);
  const [query, setQuery] = React.useState(initialQuery);
  const listboxId = `tree-authoring-listbox-${instanceId}`;
  const visibleGroups = React.useMemo(() => {
    const needle = query.trim().toLowerCase();
    return groups.map((group) => ({
      ...group,
      options: (group.options || []).filter((option) => (
        typeof option.matchesQuery === 'function'
          ? option.matchesQuery(query)
          : (!needle || option.alwaysVisible || optionText(option).includes(needle))
      )),
    })).filter((group) => group.options.length > 0);
  }, [groups, query]);
  const options = React.useMemo(
    () => visibleGroups.flatMap((group) => group.options),
    [visibleGroups],
  );
  const [activeId, setActiveId] = React.useState(null);
  const effectiveActiveId = options.some(({ id }) => id === activeId) ? activeId : options[0]?.id || null;

  React.useEffect(() => {
    inputRef.current?.focus();
  }, []);

  React.useEffect(() => {
    setActiveId((current) => (
      options.some(({ id }) => id === current) ? current : options[0]?.id || null
    ));
  }, [options]);

  const close = React.useCallback((reason = 'dismiss') => {
    onClose?.(reason);
    restoreFocus(openerRef);
  }, [onClose, openerRef]);

  const commit = React.useCallback((option) => {
    if (!option || option.disabled) return;
    const keepOpen = onSelect?.(option, { query }) === false;
    if (!keepOpen) close('select');
  }, [close, onSelect, query]);

  const move = (direction) => {
    if (options.length === 0) return;
    const currentIndex = Math.max(0, options.findIndex(({ id }) => id === effectiveActiveId));
    let nextIndex = currentIndex;
    if (direction === 'first') nextIndex = 0;
    else if (direction === 'last') nextIndex = options.length - 1;
    else nextIndex = (currentIndex + direction + options.length) % options.length;
    setActiveId(options[nextIndex].id);
  };

  const handleKeyDown = (event) => {
    event.stopPropagation();
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      move(event.key === 'ArrowDown' ? 1 : -1);
      return;
    }
    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      move(event.key === 'Home' ? 'first' : 'last');
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      commit(options.find(({ id }) => id === effectiveActiveId));
    }
  };

  return (
    <div
      data-tree-popup="true"
      className={`z-50 min-w-[18rem] rounded-lg border border-divider bg-bg-1 p-2 shadow-xl ${className}`}
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
      onKeyDown={handleKeyDown}
    >
      <input
        ref={inputRef}
        role="combobox"
        type="text"
        aria-label={ariaLabel}
        aria-autocomplete="list"
        aria-expanded="true"
        aria-controls={listboxId}
        aria-activedescendant={effectiveActiveId ? `${listboxId}-option-${effectiveActiveId}` : undefined}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={placeholder}
        className="mb-2 w-full rounded border border-divider bg-bg-2 px-2 py-1.5 text-xs text-fg-1 outline-none focus-visible:ring-1 focus-visible:ring-primary"
      />
      <div id={listboxId} role="listbox" aria-label={ariaLabel} className="max-h-72 overflow-y-auto">
        {visibleGroups.map((group) => {
          const labelId = `${listboxId}-group-${group.id}`;
          return (
            <div key={group.id} role="group" aria-labelledby={labelId} className="mb-1 last:mb-0">
              <div id={labelId} className="px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-fg-3">
                {group.label}
              </div>
              {group.options.map((option) => {
                const selected = option.id === effectiveActiveId;
                return (
                  <div
                    key={option.id}
                    id={`${listboxId}-option-${option.id}`}
                    role="option"
                    aria-selected={selected}
                    aria-disabled={option.disabled || undefined}
                    data-authoring-item-id={option.item?.id || option.id}
                    onMouseMove={() => { if (!option.disabled) setActiveId(option.id); }}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => commit(option)}
                    className={`cursor-pointer rounded text-xs ${selected ? 'bg-primary/10 text-fg-1' : 'text-fg-2 hover:bg-bg-2'} ${option.disabled ? 'cursor-not-allowed opacity-50' : ''}`}
                  >
                    {renderOption ? renderOption(option) : <div className="px-2 py-1.5">{option.label}</div>}
                  </div>
                );
              })}
            </div>
          );
        })}
        {options.length === 0 ? <div className="px-2 py-3 text-xs text-fg-3">{emptyLabel}</div> : null}
      </div>
    </div>
  );
}
